// app/features/ai-workflows/services_and_hooks/usePersonalizedUseCasesHook.ts
// v2

// app/features/personalized-use-cases/services_and_hooks/usePersonalizedUseCasesHook.ts
"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

// ### CODE CHANGES START ###
// We get EVERYTHING from our one true auth hook.
import { useAuthHook } from "@/app/auth/services_and_hooks/useAuthHook";
import { usePersonalizedUseCasesStore } from "@/app/features/ai-workflows/services_and_hooks/personalizedUseCasesStore";
import { posthogClientService } from "@/app/features/posthog/services_and_hooks/posthogClientService";
import { activationService } from "@/app/features/user-profile/services_and_hooks/activationService";
import { useNotificationStore } from "@/app/shared/store/notificationsStore";
// The dependency on the deleted useUserHook is GONE.
// ### CODE CHANGES FINISH ###
import { Database } from "@/types/supabase";

import {
  PaginationOptions,
  PersonalizedUseCaseCreationInput,
} from "../personalized-use-case.interface";
import { ProblemSubmissionData } from "../personalizedUseCasesTypes";
import { deriveValidPage } from "./deriveValidPage";
import {
  useDeleteUseCaseMutationHook,
  useGenerateUseCaseMutationHook,
  useRestartUseCaseMutationHook,
  useSubmitFeedbackMutationHook,
  useUserPersonalizedUseCasesPaginatedQueryHook,
} from "./usePersonalizedUseCasesQueryHook";

type PersonalizedUseCase =
  Database["public"]["Tables"]["personalized_use_cases"]["Row"];

// 🔥 REMOVED: Timeout detection now handled by PostgreSQL function
// See timeout_function.sql for the database-level timeout handling

/**
 * A unified hook to manage state and data fetching for Personalized Use Cases.
 * It combines Zustand state management with TanStack Query data fetching.
 */
export const usePersonalizedUseCasesHook = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  const {
    user,
    isLoading: isAuthLoading,
    organizations,
    primaryOrganization,
  } = useAuthHook();

  // 🔥 FIX: Organizations loading state - use auth loading as proxy since they load together
  const isLoadingOrganizations = isAuthLoading;

  const userId = user?.id;

  // ### CODE CHANGES FINISH ###

  const storeState = usePersonalizedUseCasesStore(
    useShallow((state: any) => ({
      viewMode: state.viewMode,
      setViewMode: state.setViewMode,
      currentStep: state.currentStep,
      setCurrentStep: state.setCurrentStep,
      selectedOrganization: state.selectedOrganization,
      setSelectedOrganization: state.setSelectedOrganization,
      problemData: state.problemData,
      updateProblemData: state.updateProblemData,
      clearProblemData: state.clearProblemData,
      generationStatus: state.generationStatus,
      setGenerationStatus: state.setGenerationStatus,
      generatedUseCase: state.generatedUseCase,
      setGeneratedUseCase: state.setGeneratedUseCase,
      resetFlow: state.resetFlow,
    })),
  );

  const {
    viewMode,
    setViewMode,
    currentStep,
    setCurrentStep,
    selectedOrganization,
    setSelectedOrganization,
    problemData,
    updateProblemData,
    clearProblemData,
    generationStatus: zustandGenerationStatus,
    setGenerationStatus: setZustandGenerationStatus,
    generatedUseCase: zustandGeneratedUseCase,
    resetFlow,
  } = storeState;

  // 🔥 NEW: Auto-select organization when user has only one
  useEffect(() => {
    // Only auto-select if:
    // 1. Organizations are loaded (not loading and not empty)
    // 2. User has exactly one organization
    // 3. No organization is currently selected
    // 4. User is on the organization selection step
    if (
      !isLoadingOrganizations &&
      organizations &&
      organizations.length === 1 &&
      !selectedOrganization &&
      currentStep === "organization_selection"
    ) {
      // console.log('[usePersonalizedUseCasesHook] Auto-selecting single organization:', organizations[0].name);
      setSelectedOrganization(organizations[0]);
      setCurrentStep("problem_description");
    }
  }, [
    isLoadingOrganizations,
    organizations,
    selectedOrganization,
    currentStep,
    setSelectedOrganization,
    setCurrentStep,
  ]);

  // Create a custom hook that handles both pagination and data fetching
  const usePaginatedUseCases = (userId?: string) => {
    const [requestedPage, setRequestedPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState<12 | 48>(12);

    const paginationOptions: PaginationOptions = useMemo(
      () => ({
        page: requestedPage,
        limit: recordsPerPage,
        offset: (requestedPage - 1) * recordsPerPage,
      }),
      [requestedPage, recordsPerPage],
    );

    // 🔥 FIX: Only call the query hook when userId is available to prevent race condition
    const queryResult = useUserPersonalizedUseCasesPaginatedQueryHook(
      userId || undefined, // Ensure we pass undefined instead of null/empty string
      paginationOptions,
    );
    const { totalCount } = queryResult;
    const currentPage = deriveValidPage(
      requestedPage,
      totalCount,
      recordsPerPage,
    );
    if (currentPage !== requestedPage) {
      setRequestedPage(currentPage);
    }
    const safeTotal = totalCount ?? 0;

    const paginationState = useMemo(() => {
      const totalPages = Math.ceil(safeTotal / recordsPerPage);

      return {
        currentPage,
        recordsPerPage,
        totalRecords: safeTotal,
        totalPages,
        paginationOptions: {
          page: currentPage,
          limit: recordsPerPage,
          offset: (currentPage - 1) * recordsPerPage,
        },
        goToPage: (page: number) =>
          setRequestedPage(Math.max(1, Math.min(page, totalPages))),
        goToNextPage: () =>
          setRequestedPage((prev) => Math.min(prev + 1, totalPages)),
        goToPrevPage: () => setRequestedPage((prev) => Math.max(prev - 1, 1)),
        changeRecordsPerPage: (newLimit: 12 | 48) => {
          setRecordsPerPage(newLimit);
          setRequestedPage(1);
        },
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1,
      };
    }, [currentPage, recordsPerPage, safeTotal]);

    return { ...queryResult, pagination: paginationState };
  };
  const {
    data: userPersonalizedUseCasesData,
    totalCount,
    isLoading: isLoadingUserUseCases,
    isError: isErrorUserUseCases,
    error: errorUserUseCases,
    refetch: refetchUserUseCases,
    pagination,
  } = usePaginatedUseCases(userId); // 🔥 FIX: Pass userId to prevent race condition

  // 🔥 DEBUG: Add logging to track data loading
  // useEffect(() => {
  //   // Only log when there are significant changes or issues
  //   if (isErrorUserUseCases) {
  //     console.error('[usePersonalizedUseCasesHook] Error loading use cases:', {
  //       userId,
  //       error: errorUserUseCases?.message
  //     });
  //   } else if (!isAuthLoading && !isLoadingUserUseCases && userPersonalizedUseCasesData?.length) {
  //     console.log('[usePersonalizedUseCasesHook] Data loaded successfully:', {
  //       userId,
  //       useCasesCount: userPersonalizedUseCasesData?.length,
  //       totalCount
  //     });
  //   }
  // }, [userId, isAuthLoading, isLoadingUserUseCases, isErrorUserUseCases, userPersonalizedUseCasesData?.length, totalCount, errorUserUseCases]);

  // 🔥 REMOVED: The first useEffect has been moved into the usePagination hook itself
  // This follows the principle of making hooks responsible for their own internal consistency

  const {
    mutateAsync: generateUseCaseMutate,
    isPending: isGenerating,
    isError: isGenerationError,
    error: generationError,
    reset: resetGenerationMutation,
  } = useGenerateUseCaseMutationHook();
  const submitFeedbackMutation = useSubmitFeedbackMutationHook();
  const restartUseCaseMutation = useRestartUseCaseMutationHook();
  const deleteUseCaseMutation = useDeleteUseCaseMutationHook();

  const selectOrganizationAction = useCallback(
    (organization: any) => {
      setSelectedOrganization(organization);
      setCurrentStep("problem_description");
    },
    [setSelectedOrganization, setCurrentStep],
  );

  const updateProblemAction = useCallback(
    (data: Partial<ProblemSubmissionData>) => {
      updateProblemData(data);
    },
    [updateProblemData],
  );

  const restartUseCaseAction = useCallback(
    async (useCaseId: string) => {
      return await restartUseCaseMutation.mutateAsync({ useCaseId });
    },
    [restartUseCaseMutation],
  );

  const deleteUseCaseAction = useCallback(
    async (useCaseId: string) => {
      return await deleteUseCaseMutation.mutateAsync({ useCaseId });
    },
    [deleteUseCaseMutation],
  );

  const startGenerationAction = useCallback(
    async (formData: ProblemSubmissionData) => {
      updateProblemData(formData);

      if (
        !userId ||
        !selectedOrganization?.id ||
        !formData.problemDescription
      ) {
        setZustandGenerationStatus("failed");
        return;
      }

      // Track workflow generation started
      try {
        const { PostHogTrackingService } = await import(
          "@/app/features/posthog/services_and_hooks/posthogTrackingService"
        );
        if (user) {
          await PostHogTrackingService.trackWorkflowGenerationStarted({
            user_id: user.id,
            email: user.email || "",
            organization_id: selectedOrganization.id,
            problem_description: formData.problemDescription,
            level: formData.level || "regular",
            desired_outcome: formData.desiredOutcome,
          });
        }
      } catch (error) {
        console.error(
          "[usePersonalizedUseCasesHook] Failed to track workflow generation started:",
          error,
        );
      }

      const inputForNextApi: PersonalizedUseCaseCreationInput = {
        problemDescription: formData.problemDescription,
        organizationId: selectedOrganization.id,
        desiredOutcome: formData.desiredOutcome || undefined,
        // additionalContext: formData.additionalContext || undefined,
        level: formData.level || "regular",
      };

      try {
        resetGenerationMutation();

        const generationInitiationResponse = await generateUseCaseMutate({
          inputData: inputForNextApi,
        });

        if (generationInitiationResponse?.id) {
          // Track AI workflow creation
          if (user) {
            posthogClientService.track("user_created_ai_workflow", {
              user_id: user.id,
              email: user.email || "",
              workflow_id: generationInitiationResponse.id,
              problem_description: formData.problemDescription,
              organization_id: selectedOrganization.id,
              level: formData.level || "regular",
              desired_outcome: formData.desiredOutcome,
            });
          }

          if (userId) {
            // 🔥 CRITICAL: Optimistically update the cache with the newly created workflow
            // This ensures it shows immediately in the UI even before the refetch completes
            queryClient.setQueryData(
              ["userPersonalizedUseCasesPaginated", userId],
              (oldData: any) => {
                if (!oldData) return oldData;
                // Add the new workflow to the beginning of the list
                return {
                  ...oldData,
                  data: [generationInitiationResponse, ...(oldData.data || [])],
                  totalCount: (oldData.totalCount || 0) + 1,
                };
              },
            );

            // Also update non-paginated cache for backward compatibility
            queryClient.setQueryData(
              ["userPersonalizedUseCases", userId],
              (oldData: PersonalizedUseCase[] | undefined) => {
                if (!oldData) return [generationInitiationResponse];
                return [generationInitiationResponse, ...oldData];
              },
            );

            // Then invalidate and refetch to get the latest from DB
            queryClient.invalidateQueries({
              queryKey: ["userPersonalizedUseCases", userId],
            });
            queryClient.invalidateQueries({
              queryKey: ["userPersonalizedUseCasesPaginated", userId],
            });

            // Refetch in background (don't await to avoid blocking the redirect)
            queryClient.refetchQueries({
              queryKey: ["userPersonalizedUseCases", userId],
            });
            queryClient.refetchQueries({
              queryKey: ["userPersonalizedUseCasesPaginated", userId],
            });
          }

          // 🔥 NEW: Check activation complete (may send welcome newsletter if all steps done)
          try {
            console.log(
              "[usePersonalizedUseCasesHook] Checking activation complete after workflow creation",
            );
            const activationResult =
              await activationService.triggerActivationComplete();
            console.log(
              "[usePersonalizedUseCasesHook] Activation complete result:",
              activationResult,
            );

            // CRITICAL: Refetch activation status after checking completion to update UI
            await queryClient.invalidateQueries({
              queryKey: ["activationStatus"],
            });
            await queryClient.refetchQueries({
              queryKey: ["activationStatus"],
            });
            console.log(
              "[usePersonalizedUseCasesHook] Activation status refetched after completion check",
            );
          } catch (activationError) {
            // Don't fail workflow creation if activation check fails
            console.error(
              "[usePersonalizedUseCasesHook] Failed to check activation:",
              activationError,
            );
          }

          resetFlow();

          // 🔥 IMPROVED: Immediate redirect since we've optimistically updated the cache
          router.push("/ai-workflows");
        } else {
          setZustandGenerationStatus("failed");
        }
      } catch (error: any) {
        setZustandGenerationStatus("failed");
      }
    },
    [
      userId,
      selectedOrganization?.id,
      updateProblemData,
      setZustandGenerationStatus,
      generateUseCaseMutate,
      resetGenerationMutation,
      resetFlow,
      queryClient,
      user,
      router,
    ],
  );

  /**
   * Submits feedback for a use case.
   * This prepares the simplified payload that the mutation expects.
   */
  const submitFeedback = async (feedbackData: {
    use_case_id: string;
    feedback_text: string | null;
    is_useful: boolean;
    regenerate_requested: boolean;
  }) => {
    if (!user) {
      console.error("User not authenticated to submit feedback.");
      throw new Error("You must be logged in to submit feedback.");
    }

    const payload = {
      use_case_id: feedbackData.use_case_id,
      feedback_text: feedbackData.feedback_text,
      is_useful: feedbackData.is_useful,
      is_positive_feedback: null, // We're not collecting this yet
      regenerate_requested: feedbackData.regenerate_requested,
      use_case_type: "personalized" as const,
      user_id: user.id,
    };

    console.log(
      "Dispatching feedback submission with simplified payload:",
      payload,
    );
    // Correctly call the mutation function
    return submitFeedbackMutation.mutateAsync(payload);
  };

  /**
   * Resets the creation flow and switches the view to the 'create' mode.
   */
  const handleCreateNew = () => {
    console.log("handleCreateNew triggered from hook");
    resetFlow();
    setViewMode("create");
  };

  // 🔥 FIX: Proper loading state logic for skeleton display
  // Show loading when:
  // 1. Auth is loading (initial state), OR
  // 2. User exists but data is loading, OR
  // 3. Auth finished but no user ID available (shouldn't happen for protected routes)
  const isLoading =
    isAuthLoading ||
    (userId && isLoadingUserUseCases) ||
    (!isAuthLoading && !userId);

  return {
    user,
    isAuthLoading,
    viewMode,
    setViewMode,
    currentStep,
    setCurrentStep,
    selectedOrganization,
    problemData,
    clearProblemData,
    generationStatus: zustandGenerationStatus,
    generatedUseCase: zustandGeneratedUseCase,
    resetFlow,
    userPersonalizedUseCases: userPersonalizedUseCasesData ?? [],
    totalCount,
    isLoadingUserUseCases,
    isLoading, // 🔥 FIX: Combined loading state for proper skeleton display
    isErrorUserUseCases,
    errorUserUseCases,
    refetchUserUseCases,
    // Pagination data and controls
    pagination,
    // ### CODE CHANGES START ###
    // These now come directly from the main useAuthHook
    organizations: organizations ?? [],
    isLoadingOrganizations,
    primaryOrganization,
    // There are no separate error/refetch functions for orgs anymore,
    // as it's all handled by the main app data query in useAuthHook.
    // ### CODE CHANGES FINISH ###
    isGenerating,
    isGenerationError,
    generationError,
    resetGenerationMutation,
    isSubmittingFeedback: submitFeedbackMutation.isPending,
    feedbackError: submitFeedbackMutation.error,
    isRestartingUseCase: restartUseCaseMutation.isPending,
    restartError: restartUseCaseMutation.error,
    isDeletingUseCase: deleteUseCaseMutation.isPending,
    deleteError: deleteUseCaseMutation.error,
    selectOrganization: selectOrganizationAction,
    updateProblem: updateProblemAction,
    startGeneration: startGenerationAction,
    submitFeedback,
    restartUseCase: restartUseCaseAction,
    deleteUseCase: deleteUseCaseAction,
    handleCreateNew,
  };
};
